import React, { Fragment } from 'react';
import { Col, Collapse, Form, Input, InputNumber, Row, Select } from 'antd';
import { FormattedMessage } from 'react-intl';

import FormDependency, { shouldUpdateDependency } from 'components/FormDependency';
import SurveyPreview from 'components/SurveyPreview';
import get from 'get-value';
import TranslationTabs from 'components/TranslationTabs';
import DeviceTypeIcons from 'components/DeviceTypeIcons';
import { DeviceType, SurveySetting, SurveyType } from 'shared/services/prisma/types';
import InputInfoCircle from 'components/InputInfoCircle';
import messages from './messages';

const { Panel } = Collapse;

const settingsOptions = [
  { label: 'Default settings', value: SurveySetting.Default },
  { label: 'Use desktop settings', value: SurveySetting.Desktop },
  { label: 'Use tablet settings', value: SurveySetting.Tablet },
  { label: 'Use mobile settings', value: SurveySetting.Mobile },
  { label: 'Custom settings', value: SurveySetting.Custom },
];

const translationSettingsOptions = [
  { label: 'Use primary language settings', value: SurveySetting.Primary },
  ...settingsOptions,
];

function getOptions({ device, isTranslation }) {
  return (isTranslation ? translationSettingsOptions : settingsOptions).filter(o => o.value !== SurveySetting[device]);
}

const DisplayStyleForm = ({ namePath, disabled }) => (
  <Row gutter={16}>
    <Col span={8}>
      <Form.Item name={[...namePath, 'container']} label="Container">
        <Select options={[{ label: 'Overlay modal', value: 'popupLayer' }, { label: 'Popup window', value: 'popupWindow' }]} disabled={disabled} />
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item name={[...namePath, 'popupWidth']} label={<FormattedMessage {...messages.popupWidth} />}>
        <InputNumber
          min={0}
          addonAfter="px"
          disabled={disabled}
        />
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item name={[...namePath, 'popupHeight']} label={<FormattedMessage {...messages.popupHeight} />}>
        <InputNumber
          min={0}
          addonAfter="px"
          disabled={disabled}
        />
      </Form.Item>
    </Col>
  </Row>
);

const DesignForm = ({ namePath, disabled }) => (
  <Row gutter={16}>
    <Col span={8}>
      <Form.Item name={[...namePath, 'quizMode']} label="Presentation mode">
        <Select options={[{ label: 'Pages', value: false }, { label: 'Quiz', value: true }]} disabled={disabled} />
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item name={[...namePath, 'useToggleButtons']} label="Option style">
        <Select options={[{ label: 'Standard', value: false }, { label: 'Buttons', value: true }]} disabled={disabled} />
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item name={[...namePath, 'showProgress']} label="Progress bar">
        <Select options={[{ label: 'Hide', value: false }, { label: 'Show', value: true }]} disabled={disabled} />
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item name={[...namePath, 'logoSource']} label="Logo">
        <Select options={[{ label: 'Account', value: 'CUSTOMER' }, { label: 'Url', value: 'URL' }, { label: 'None', value: 'NONE' }]} disabled={disabled} />
      </Form.Item>
    </Col>
    <Col span={8}>
      <Form.Item name={[...namePath, 'logoHeight']} label="Logo height">
        <InputNumber addonAfter="px" disabled={disabled} />
      </Form.Item>
    </Col>

    <FormDependency name={[...namePath, 'logoSource']} show={value => value === 'URL'}>
      <Col span={24}>
        <Form.Item name={[...namePath, 'logoUrl']} label={<FormattedMessage {...messages.logoUrl} />}>
          <Input disabled={disabled} />
        </Form.Item>
      </Col>
    </FormDependency>
  </Row>
);

const DesignSettings = ({ name, survey, device, initialValues, isTranslation }) => {
  const namePath = [name, survey.id, device];
  const setting = Form.useWatch([...namePath, 'setting']);

  const showDisplayStyle = (survey.type === SurveyType.General || (survey.type === SurveyType.Targeted && ['delayed', 'existingButton'].includes(get(initialValues, ['audience', survey.id, device, 'type']))));
  const showForm = (setting === SurveySetting.Custom || (setting === SurveySetting.Default));

  return (
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item noStyle name={[...namePath, 'distributionId']}>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name={[...namePath, 'setting']}>
          <Select options={getOptions({ device, isTranslation })} />
        </Form.Item>
        {showForm && (
          <>
            {showDisplayStyle && (
            <DisplayStyleForm namePath={namePath} disabled={setting !== SurveySetting.Custom} />
            )}
            <DesignForm namePath={namePath} disabled={setting !== SurveySetting.Custom} />
          </>
        )}
      </Col>

      <Col span={12} style={{ display: 'flex', minHeight: 500 }}>
        <Form.Item noStyle shouldUpdate={shouldUpdateDependency(namePath)}>
          {({ getFieldValue }) => <SurveyPreview {...getFieldValue(namePath)} />}
        </Form.Item>
      </Col>
    </Row>
  );
};

const DeviceSettings = (props) => (
  <Collapse accordion defaultActiveKey={DeviceType.Desktop}>
    {Object.values(DeviceType).map(d => {
      const device = d.toLowerCase();
      const namePath = [props.name, props.survey.id, device, 'setting'];

      return (
        <Panel
          header={(
            <>
              <DeviceTypeIcons deviceTypes={[d]} />
              <FormattedMessage {...messages[device]} />
            </>
          )}
          extra={(
            <Form.Item noStyle shouldUpdate={shouldUpdateDependency(namePath)}>
              {({ getFieldValue }) => <FormattedMessage {...messages[getFieldValue(namePath)]} />}
            </Form.Item>
          )}
          key={d}
        >
          <DesignSettings device={device} {...props} />
        </Panel>
      );
    })}
  </Collapse>
);

const DesignStep = ({ name, survey, translations, initialValues }) => (
  <>
    <h2>
      Survey appearance
      <InputInfoCircle title={<FormattedMessage {...messages.appearanceHelpText} />} iconStyle={{ marginLeft: 8, width: 16 }} />
    </h2>

    <TranslationTabs
      primary={survey}
      translations={translations}
      primaryContent={<DeviceSettings name={name} survey={survey} initialValues={initialValues} />}
      translationsContent={translations.map(translation => <DeviceSettings name={name} survey={translation} isTranslation />)}
      singleColumn
    />
  </>

);
export default DesignStep;
